import { useMemo } from 'react';
import { useAttorneySubscription } from './useAttorneySubscription';

/**
 * Freemium model:
 *  - `free` is the new permanent baseline (replaces both legacy `free_profile` and the deprecated `trial`).
 *  - Paid tiers unlock all gated features and remove all quotas.
 *  - `free_profile` and `trial` are retained as type aliases only for backwards-compatibility with cached data;
 *    they are normalized to `free` at runtime.
 */
export type AccountTier = 'free' | 'free_profile' | 'trial' | 'standard' | 'professional' | 'enterprise' | 'ultimate';

const PAID_TIERS: AccountTier[] = ['standard', 'professional', 'enterprise', 'ultimate'];

// Tabs accessible on the free profile tier (graduated access)
const FREE_PROFILE_TABS = new Set([
  'overview',
  'profile',
  'subscription',
  'help',
  'security',
  'rewards',
  'notifications',
  'communications',
  'ai',
]);

// Tabs that require a paid tier. Free tier (freemium) keeps the bulk of
// practice management — caps (3 cases / 3 matters / 3 clients / no invoicing
// / connected-only messaging) are enforced inside the views themselves.
// Only invoicing, advanced reporting, CRM, team, and marketing remain paid.
const PAID_ONLY_TABS = new Set([
  'billing',
  'payouts',
  'time-templates',
  'time-analytics',
  'time-approvals',
  'approver-settings',
  'crm',
  'contact-details',
  'team',
  'analytics',
  'marketing',
]);

// Tabs visible but with limited functionality on free tier
const CONNECTION_TABS = new Set([
  'connections',
  'escalation-details',
  'cocounsel-inquiries',
  'reviews',
]);

export const useAttorneyAccountTier = () => {
  const { subscriptionData, loading, isAttorney, lawyerData } = useAttorneySubscription();

  const accountTier = useMemo((): AccountTier => {
    if (!subscriptionData || !isAttorney) return 'free';

    const tier = subscriptionData.subscription_tier?.toLowerCase() || '';
    const status = subscriptionData.subscription_status?.toLowerCase() || '';

    // Active paid subscription (check first so paid users never fall into 'free')
    if (tier.includes('ultimate')) return 'ultimate';
    if (tier.includes('enterprise')) return 'enterprise';
    if (tier.includes('professional')) return 'professional';
    if (tier.includes('standard') && !tier.includes('trial') && !tier.includes('free')) return 'standard';
    if (status === 'active' || status === 'paid') return 'standard';

    // Everything else collapses to the new permanent Free tier
    // (legacy trial / free_profile / expired states all land here)
    return 'free';
  }, [subscriptionData, isAttorney]);

  // Backwards-compatible alias so existing call sites keep working.
  const isFreeProfile = accountTier === 'free';
  const hasPaidAccess = PAID_TIERS.includes(accountTier);

  const isTabLocked = (tab: string): boolean => {
    if (hasPaidAccess) return false;
    return PAID_ONLY_TABS.has(tab);
  };

  const isTabVisible = (tab: string): boolean => {
    // All tabs are visible, but locked ones show upgrade prompt
    return true;
  };

  // Connection/co-counsel acceptance requires paid tier
  const canAcceptConnections = hasPaidAccess;
  const canAcceptCoCounsel = hasPaidAccess;

  // Free profiles can view connections but not accept them
  const isConnectionTabReadOnly = (tab: string): boolean => {
    if (hasPaidAccess) return false;
    return CONNECTION_TABS.has(tab);
  };

  // Hard fallbacks mirror the canonical Free row (subscription_tiers.tier_key='lawyer_free')
  // and are only used if the edge function omits them (e.g. cached payload pre-rollout).
  const FREE_FALLBACK_CAPS = {
    case_limit: 3,
    matter_limit: 3,
    monthly_connection_limit: 3,
    messaging_scope: 'connected_only' as const,
    time_tracking_enabled: true,
    invoicing_enabled: false,
    ai_questions_per_month: 25,
    client_limit: 3,
    attorney_limit: 1,
  };

  const tierCaps = hasPaidAccess
    ? {
        case_limit: null,
        matter_limit: null,
        monthly_connection_limit: null,
        messaging_scope: 'unlimited' as const,
        time_tracking_enabled: true,
        invoicing_enabled: true,
        ai_questions_per_month: subscriptionData?.ai_questions_per_month ?? null,
        client_limit: subscriptionData?.client_limit ?? null,
        attorney_limit: subscriptionData?.attorney_limit ?? null,
      }
    : {
        case_limit: subscriptionData?.case_limit ?? FREE_FALLBACK_CAPS.case_limit,
        matter_limit: subscriptionData?.matter_limit ?? FREE_FALLBACK_CAPS.matter_limit,
        monthly_connection_limit:
          subscriptionData?.monthly_connection_limit ?? FREE_FALLBACK_CAPS.monthly_connection_limit,
        messaging_scope: subscriptionData?.messaging_scope ?? FREE_FALLBACK_CAPS.messaging_scope,
        time_tracking_enabled:
          subscriptionData?.time_tracking_enabled ?? FREE_FALLBACK_CAPS.time_tracking_enabled,
        invoicing_enabled:
          subscriptionData?.invoicing_enabled ?? FREE_FALLBACK_CAPS.invoicing_enabled,
        ai_questions_per_month:
          subscriptionData?.ai_questions_per_month ?? FREE_FALLBACK_CAPS.ai_questions_per_month,
        client_limit: subscriptionData?.client_limit ?? FREE_FALLBACK_CAPS.client_limit,
        attorney_limit: subscriptionData?.attorney_limit ?? FREE_FALLBACK_CAPS.attorney_limit,
      };

  return {
    accountTier,
    isFreeProfile,
    hasPaidAccess,
    isTabLocked,
    isTabVisible,
    canAcceptConnections,
    canAcceptCoCounsel,
    isConnectionTabReadOnly,
    loading,
    subscriptionData,
    lawyerData,
    tierCaps,
    tierFeatures: subscriptionData?.tier_features ?? [],
  };
};
